import Skeleton from '@mui/material/Skeleton';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React from 'react';

import { Tooltip } from '@swf/poker';

import useCheckUserClubRole from '../../hooks/useCheckUserClubRole';
import useHGGameUrl from '../../hooks/useHGGameUrl';
import type { HGPokerGameTile } from '../../types/common';
import { HGGameType, UserClubRole } from '../../types/common';
import Image from '../Image/Image';

import Footer from './Footer/Footer';
import { TXTCLI_Recurring_Tournament, TXTCLI_Video_Chat_Enabled } from './GameTile.messages.json';
import styles from './GameTile.scss';
import GameTileCoachMark from './GameTileCoachMark/GameTileCoachMark';
import RegisterButton from './RegisterButton/RegisterButton';
import SelectGame from './SelectGame/SelectGame';
import Tags from './Tags/Tags';
import Unsupported from './Unsupported/Unsupported';
import Wrapper from './Wrapper/Wrapper';
import RecurringIcon from './assets/recurring.svg';
import VideoIcon from './assets/video.svg';

const GameTile: React.FC<HGPokerGameTile> = ({
  imageUrl,
  tags,
  link,
  attributes,
  server: serverAddress,
  serverObj: serverObjectName,
  type,
  id,
  clubId,
  supported,
  name,
  maxPlayers,
  hasVideoSupport,
  tournamentState,
  playersSeated,
  entrants,
  isRecurring,
  isCashGamesActive,
  isTournsActive
}) => {
  const isAdminOrManager = useCheckUserClubRole(clubId, [UserClubRole.Manager, UserClubRole.Admin]);
  const canSelect =
    isAdminOrManager && (type === HGGameType.CashGame || type === HGGameType.Tournament);
  const tableUrl = useHGGameUrl({
    type,
    serverAddress,
    serverObjectName,
    hasVideoSupport
  });
  const isCashGame = type === HGGameType.CashGame;
  const isTournament = type === HGGameType.Tournament;
  const unsupported =
    useFeature(FeatureFlag.PKRHomeGamesGameTilesUnsupported) ||
    (typeof supported === 'boolean' && !supported);

  return (
    <Wrapper
      className={styles.container}
      tableUrl={!unsupported ? tableUrl : undefined}
      link={link}
      id={id}
      clubId={clubId}
      unsupported={
        unsupported || (isCashGame && !isCashGamesActive) || (isTournament && !isTournsActive)
      }
    >
      <GameTileCoachMark />
      <div className={styles.imgContainer}>
        <Image
          loader={<Skeleton className={styles.loading} variant="rectangular" />}
          alt="Game image"
          data-testid="game-item-image"
          imageUrl={imageUrl}
        />
        {type === HGGameType.Tournament && (
          <RegisterButton id={id} tournamentState={tournamentState} />
        )}
        {canSelect && <SelectGame id={id} />}
      </div>
      <div className={styles.iconsContainer}>
        {hasVideoSupport && (
          <Tooltip
            content={<Translation {...TXTCLI_Video_Chat_Enabled} />}
            arrowClassName={styles.tooltipArrow}
            tooltipClassName={styles.tooltip}
            placement="top"
            refElementClassName={styles.tooltipWrapper}
          >
            <div className={styles.icon} data-testid="video">
              <VideoIcon />
            </div>
          </Tooltip>
        )}
        {isRecurring && (
          <Tooltip
            content={<Translation {...TXTCLI_Recurring_Tournament} />}
            arrowClassName={styles.tooltipArrow}
            tooltipClassName={styles.tooltip}
            placement="top"
            refElementClassName={styles.tooltipWrapper}
          >
            <div className={styles.icon} data-testid="recurring">
              <RecurringIcon />
            </div>
          </Tooltip>
        )}
      </div>
      <Tags tags={tags} />
      <Footer
        tournamentState={tournamentState}
        canSelect={canSelect}
        id={id}
        attributes={attributes}
        tableName={name}
        maxPlayers={maxPlayers}
        entrants={entrants}
        playersSeated={playersSeated}
      />
      {unsupported || (isCashGame && !isCashGamesActive) || (isTournament && !isTournsActive) ? (
        <Unsupported />
      ) : null}
    </Wrapper>
  );
};

export default GameTile;
